import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card } from "@/components/admin/card";
import { AToggle, FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { AiAssist } from "@/components/ai/ai-assist";
import { type ToastApi, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useArchiveCategory,
	useCategoriesTree,
	useCreateCategory,
	useDeleteCategory,
	useDeleteCategoryWithProducts,
	useReassignDeleteCategory,
	useUnarchiveCategory,
	useUpdateCategory,
} from "@/hooks/use-catalog";
import { aiApi, type CategorySeoCopy } from "@/lib/ai/ai-api";
import { categoriesApi, mediaApi } from "@/lib/catalog/catalog-api";
import type { Category, MediaAsset } from "@/lib/catalog/types";
import type { TagClass } from "@/lib/status";

type TreeFilter = "all" | "active" | "draft" | "archived";

interface StatusBadge {
	l: string;
	cls: TagClass;
}

const statusForCategory = (c: Category): StatusBadge => {
	if (!c.isActive) return { l: "Brouillon", cls: "neutral" };
	if (c.productsCount < 5) return { l: "À enrichir", cls: "warning" };
	return { l: "Actif", cls: "success" };
};

const formatCount = (n: number): string =>
	n.toLocaleString("fr-FR").replace(/,/g, " ");

interface FlatCounts {
	total: number;
	l1: number;
	l2: number;
	l3: number;
}

function flatten(tree: Category[]): FlatCounts {
	let total = 0;
	let l1 = 0;
	let l2 = 0;
	let l3 = 0;
	tree.forEach((c) => {
		total++;
		l1++;
		(c.children ?? []).forEach((s) => {
			total++;
			l2++;
			(s.children ?? []).forEach(() => {
				total++;
				l3++;
			});
		});
	});
	return { total, l1, l2, l3 };
}

interface FoundCategory extends Category {
	path: string[];
	parent?: Category;
}

function findCategory(tree: Category[], id: string): FoundCategory | null {
	for (const c of tree) {
		if (c.id === id) return { ...c, path: [c.name] };
		for (const s of c.children ?? []) {
			if (s.id === id) return { ...s, path: [c.name, s.name], parent: c };
			for (const t of s.children ?? []) {
				if (t.id === id)
					return { ...t, path: [c.name, s.name, t.name], parent: s };
			}
		}
	}
	return null;
}

export function AdminCategoriesPage() {
	const [filter, setFilter] = useState<TreeFilter>("all");
	const showArchived = filter === "archived";
	const { data: tree = [], isLoading } = useCategoriesTree(false);
	const { data: archivedList = [] } = useCategoriesTree(true);
	const createMut = useCreateCategory();
	const updateMut = useUpdateCategory();
	const deleteMut = useDeleteCategory();
	const archiveMut = useArchiveCategory();
	const unarchiveMut = useUnarchiveCategory();
	const toast = useToast();

	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const [search, setSearch] = useState("");
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

	// Sélectionne la première racine par défaut.
	useEffect(() => {
		if (!selectedId && tree.length > 0) setSelectedId(tree[0].id);
	}, [selectedId, tree]);

	const counts = flatten(tree);
	const detail = useMemo(
		() => (selectedId ? findCategory(tree, selectedId) : null),
		[selectedId, tree],
	);

	// Form local pour l'édition.
	const [form, setForm] = useState<Partial<Category>>({});
	const [formError, setFormError] = useState<string | null>(null);
	useEffect(() => {
		if (detail) {
			setForm({
				name: detail.name,
				slug: detail.slug,
				parentId: detail.parentId,
				iconEmoji: detail.iconEmoji,
				accentColor: detail.accentColor,
				metaTitle: detail.metaTitle,
				metaDescription: detail.metaDescription,
				keywords: detail.keywords ?? [],
				ogImageMediaId: detail.ogImageMediaId,
				ogImageUrl: detail.ogImageUrl,
				heroMediaId: detail.heroMediaId,
				heroUrl: detail.heroUrl,
				heroMobileMediaId: detail.heroMobileMediaId,
				heroMobileUrl: detail.heroMobileUrl,
				heroAlt: detail.heroAlt,
				isActive: detail.isActive,
				isVisibleMobile: detail.isVisibleMobile,
				isFeatured: detail.isFeatured,
				isSearchable: detail.isSearchable,
			});
			setFormError(null);
		}
	}, [detail]);

	const toggle = (id: string) =>
		setExpanded((s) => {
			const n = new Set(s);
			if (n.has(id)) n.delete(id);
			else n.add(id);
			return n;
		});

	const expandAll = () => {
		const all = new Set<string>();
		for (const c of tree) {
			all.add(c.id);
			for (const s of c.children ?? []) {
				all.add(s.id);
			}
		}
		setExpanded(all);
	};
	const collapseAll = () => setExpanded(new Set());

	const matches = (name: string) =>
		!search || name.toLowerCase().includes(search.toLowerCase());

	const passesFilter = (node: Category): boolean => {
		if (filter === "all") return true;
		if (filter === "active") return node.isActive;
		if (filter === "draft") return !node.isActive;
		return true;
	};

	const renderNode = (
		node: Category,
		level = 0,
		parentColor: string | null = null,
	) => {
		const subs = node.children ?? [];
		const hasSubs = subs.length > 0;
		const isExp = expanded.has(node.id);
		const isSel = selectedId === node.id;
		const visible =
			!search ||
			matches(node.name) ||
			subs.some(
				(s) =>
					matches(s.name) || (s.children ?? []).some((t) => matches(t.name)),
			);
		if (!visible) return null;
		if (!passesFilter(node)) return null;

		const st = statusForCategory(node);
		const accent = node.accentColor ?? "#C6563A";

		return (
			<Fragment key={node.id}>
				{/* biome-ignore lint/a11y/noStaticElementInteractions: tree item row */}
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: tree item row */}
				<div
					className={"cat-row" + (isSel ? " selected" : "")}
					onClick={() => setSelectedId(node.id)}
					style={{ paddingLeft: 14 + level * 20 }}
				>
					<button
						type="button"
						className="cat-toggle"
						onClick={(e) => {
							e.stopPropagation();
							if (hasSubs) toggle(node.id);
						}}
						style={{ visibility: hasSubs ? "visible" : "hidden" }}
					>
						<AdminIcon n={isExp ? "chevD" : "chevR"} s={12} />
					</button>
					<div className="cat-grip">
						<AdminIcon n="dots" s={12} />
					</div>
					{level === 0 ? (
						<div
							className="cat-icon"
							style={{ background: accent + "20", color: accent }}
						>
							<span style={{ fontSize: 14 }}>{node.iconEmoji ?? "📦"}</span>
						</div>
					) : (
						<div
							className="cat-dot"
							style={{
								background: (parentColor || accent) + "40",
								borderColor: parentColor || accent,
							}}
						/>
					)}
					<div className="cat-name">
						<strong
							style={{
								fontSize: 13 + (level === 0 ? 0.5 : 0),
								fontWeight: level === 0 ? 600 : 500,
							}}
						>
							{node.name}
						</strong>
						{level === 0 && hasSubs && (
							<span className="cat-meta muted">{subs.length} sous-cat.</span>
						)}
					</div>
					<span className="cat-count tn">
						{formatCount(node.productsCount)}
					</span>
					<Tag variant={st.cls}>{st.l}</Tag>
					<button
						type="button"
						className="top-btn"
						aria-label="Actions"
						data-testid={`cat-actions-${node.id}`}
						onClick={(e) => {
							e.stopPropagation();
							const r = (
								e.currentTarget as HTMLElement
							).getBoundingClientRect();
							setMenu({
								node,
								top: r.bottom + 4,
								left: Math.max(8, r.right - 210),
							});
						}}
					>
						<AdminIcon n="dots" s={14} />
					</button>
				</div>
				{hasSubs &&
					isExp &&
					subs.map((sub) =>
						renderNode(
							sub,
							level + 1,
							level === 0 ? (node.accentColor ?? null) : parentColor,
						),
					)}
			</Fragment>
		);
	};

	const handleSave = async () => {
		if (!detail) return;
		setFormError(null);
		try {
			// Les URLs sont locales (aperçu) : on n'envoie que les ids média au backend.
			const payload = { ...form };
			for (const k of ["ogImageUrl", "heroUrl", "heroMobileUrl"] as const) {
				delete (payload as Record<string, unknown>)[k];
			}
			await updateMut.mutateAsync({ id: detail.id, payload });
		} catch (e) {
			setFormError(extractErrorMessage(e, "Erreur de sauvegarde"));
		}
	};

	// Formulaire de création (catégorie racine ou sous-catégorie).
	const [createOpen, setCreateOpen] = useState(false);
	const [createParent, setCreateParent] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const openCreate = () => {
		// « Nouvelle catégorie » crée une catégorie racine (niveau 1).
		// Les sous-catégories se créent via le menu ⋯ « Ajouter une sous-catégorie ».
		setCreateParent(null);
		setCreateOpen(true);
	};

	const submitCreate = async (name: string) => {
		try {
			const created = await createMut.mutateAsync({
				name,
				parentId: createParent?.id ?? null,
			});
			if (createParent) setExpanded((s) => new Set(s).add(createParent.id));
			setSelectedId(created.id);
			setCreateOpen(false);
			toast.success(`Catégorie « ${name} » créée.`);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Création impossible"));
		}
	};

	const handleDelete = () => {
		if (detail) setDeleteTarget(detail);
	};

	const doArchive = async (node: Category) => {
		closeMenu();
		try {
			await archiveMut.mutateAsync(node.id);
			if (selectedId === node.id) setSelectedId(null);
			toast.success(`Catégorie « ${node.name} » archivée.`);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Archivage impossible"));
		}
	};

	const doUnarchive = async (node: Category) => {
		closeMenu();
		try {
			await unarchiveMut.mutateAsync(node.id);
			toast.success(`Catégorie « ${node.name} » désarchivée.`);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Désarchivage impossible"));
		}
	};

	// ─── Menu d'actions par ligne (⋯) ─────────────────────────────────────────
	const [menu, setMenu] = useState<{
		node: Category;
		top: number;
		left: number;
	} | null>(null);
	const closeMenu = () => setMenu(null);

	const menuEdit = (node: Category) => {
		closeMenu();
		setSelectedId(node.id);
	};

	const menuAddSub = (node: Category) => {
		closeMenu();
		if (node.level >= 3) {
			toast.error(
				"Profondeur maximale atteinte (3 niveaux) — impossible d'ajouter une sous-catégorie.",
			);
			return;
		}
		setCreateParent({ id: node.id, name: node.name });
		setCreateOpen(true);
	};

	const menuToggleActive = async (node: Category) => {
		closeMenu();
		try {
			await updateMut.mutateAsync({
				id: node.id,
				payload: { isActive: !node.isActive },
			});
		} catch (e) {
			toast.error(extractErrorMessage(e, "Mise à jour impossible"));
		}
	};

	const menuDelete = (node: Category) => {
		closeMenu();
		setDeleteTarget(node);
	};

	return (
		<>
			<PageHead
				title="Catégories"
				sub={
					isLoading
						? "Chargement…"
						: `${counts.total} catégories sur 3 niveaux · ${counts.l1} principales · ${counts.l2} sous-cat. · ${counts.l3} feuilles`
				}
				actions={
					<>
						<AdminButton variant="ghost">
							<AdminIcon n="download" s={14} />
							Export arborescence
						</AdminButton>
						<AdminButton variant="primary" onClick={openCreate}>
							<AdminIcon n="plus" s={14} />
							Nouvelle catégorie
						</AdminButton>
					</>
				}
			/>

			<div className="kpi-grid">
				<div className="kpi accent">
					<div className="kpi-l">Catégories actives</div>
					<div className="kpi-v tn">
						{tree.reduce(
							(acc, c) =>
								acc +
								(c.isActive ? 1 : 0) +
								(c.children ?? []).reduce(
									(a, s) =>
										a +
										(s.isActive ? 1 : 0) +
										(s.children ?? []).filter((t) => t.isActive).length,
									0,
								),
							0,
						)}
					</div>
					<div className="kpi-foot muted">sur {counts.total} total</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Profondeur max</div>
					<div className="kpi-v tn">
						3<small> niveaux</small>
					</div>
					<div className="kpi-foot muted">contrainte CDC</div>
				</div>
				<div className="kpi warning">
					<div className="kpi-l">À enrichir</div>
					<div className="kpi-v tn">
						{tree.reduce(
							(acc, c) => acc + (c.productsCount < 5 && c.isActive ? 1 : 0),
							0,
						)}
					</div>
					<div className="kpi-foot muted">{"<"} 5 produits</div>
				</div>
				<div className="kpi">
					<div className="kpi-l">Brouillons</div>
					<div className="kpi-v tn">
						{tree.filter((c) => !c.isActive).length}
					</div>
					<div className="kpi-foot muted">Désactivées</div>
				</div>
			</div>

			<div className="cat-layout">
				<Card>
					<div className="toolbar">
						<div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
							<input
								style={{
									height: 30,
									width: "100%",
									padding: "0 12px 0 30px",
									background: "#fff",
									border: "1px solid var(--a-border)",
									borderRadius: "var(--a-r-sm)",
									fontSize: 12,
								}}
								placeholder="Filtrer dans les catégories…"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							{/* biome-ignore lint/a11y/noStaticElementInteractions: search icon container */}
							{/* biome-ignore lint/a11y/useKeyWithClickEvents: search icon container */}
							<span
								style={{
									position: "absolute",
									left: 8,
									top: 7,
									color: "var(--a-ink-mute)",
								}}
							>
								<AdminIcon n="search" s={14} />
							</span>
						</div>
						<div style={{ marginLeft: "auto" }}>
							<Segmented
								value={filter}
								onChange={setFilter}
								options={[
									{ value: "all", label: "Toutes" },
									{ value: "active", label: "Actives" },
									{ value: "draft", label: "Brouillons" },
									{
										value: "archived",
										label: `Archivées${archivedList.length ? ` (${archivedList.length})` : ""}`,
									},
								]}
							/>
						</div>
						<AdminButton variant="ghost" size="sm" onClick={expandAll}>
							Tout déplier
						</AdminButton>
						<AdminButton variant="ghost" size="sm" onClick={collapseAll}>
							Tout replier
						</AdminButton>
					</div>

					<div className="cat-tree-head">
						<span style={{ flex: 1, paddingLeft: 14 }}>Catégorie</span>
						<span style={{ width: 90, textAlign: "right" }}>Produits</span>
						<span style={{ width: 90, textAlign: "center" }}>Statut</span>
						<span style={{ width: 40 }}></span>
					</div>

					<div
						className="cat-tree"
						style={{ maxHeight: "70vh", overflowY: "auto" }}
					>
						{showArchived ? (
							archivedList.length === 0 ? (
								<div
									style={{ padding: 24, textAlign: "center" }}
									className="muted"
								>
									Aucune catégorie archivée.
								</div>
							) : (
								archivedList.map((node) => (
									// biome-ignore lint/a11y/noStaticElementInteractions: archived row item
									// biome-ignore lint/a11y/useKeyWithClickEvents: archived row item
									<div
										key={node.id}
										className="cat-row"
										onClick={() => setSelectedId(node.id)}
									>
										<div className="cat-grip">
											<AdminIcon n="box" s={12} />
										</div>
										<div className="cat-name">
											<strong style={{ fontSize: 13 }}>
												{node.iconEmoji ?? "📦"} {node.name}
											</strong>
										</div>
										<span className="cat-count tn">
											{formatCount(node.productsCount)}
										</span>
										<Tag variant="neutral">Archivée</Tag>
										<button
											type="button"
											className="top-btn"
											onClick={(e) => {
												e.stopPropagation();
												doUnarchive(node);
											}}
											title="Désarchiver"
										>
											<AdminIcon n="refresh" s={14} />
										</button>
									</div>
								))
							)
						) : (
							<>
								{tree.map((node) => renderNode(node))}
								{!isLoading && tree.length === 0 && (
									<div
										style={{ padding: 24, textAlign: "center" }}
										className="muted"
									>
										Aucune catégorie.
									</div>
								)}
							</>
						)}
					</div>
				</Card>

				<aside className="cat-detail">
					{!detail ? (
						<div style={{ padding: 24 }} className="muted">
							Sélectionne une catégorie…
						</div>
					) : (
						<>
							<div className="cat-detail-head">
								<div className="cat-crumb">
									{detail.path.map((p, i) => (
										<Fragment key={i}>
											{i > 0 && (
												<span className="sep">
													<AdminIcon n="chevR" s={10} />
												</span>
											)}
											<span
												className={i === detail.path.length - 1 ? "here" : ""}
											>
												{p}
											</span>
										</Fragment>
									))}
								</div>
								<h3>{detail.name}</h3>
								<p className="muted" style={{ fontSize: 12, margin: 0 }}>
									Code <span className="mono">{detail.code}</span> · Slug{" "}
									<span className="mono">/{detail.slug}</span>
								</p>
							</div>

							<div className="cat-detail-body">
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 10,
										marginBottom: 14,
									}}
								>
									<div className="mini-stat">
										<div className="l">Produits</div>
										<div className="v tn">
											{formatCount(detail.productsCount)}
										</div>
									</div>
									<div className="mini-stat">
										<div className="l">Sous-catégories</div>
										<div className="v tn">{detail.children?.length ?? 0}</div>
									</div>
									<div className="mini-stat">
										<div className="l">Niveau</div>
										<div className="v tn">{detail.level}</div>
									</div>
									<div className="mini-stat">
										<div className="l">Visibilité</div>
										<div className="v tn" style={{ fontSize: 13 }}>
											{detail.isActive ? "Actif" : "Masqué"}
										</div>
									</div>
								</div>

								<div className="eyebrow" style={{ marginBottom: 8 }}>
									Identité
								</div>
								<FormRow label="Nom affiché">
									<input
										className="aform-i"
										value={form.name ?? ""}
										onChange={(e) =>
											setForm((f) => ({ ...f, name: e.target.value }))
										}
									/>
								</FormRow>
								<FormRow label="Slug (URL)" helper="akwaba.cm/c/…">
									<div className="aform-with-prefix">
										<span>/c/</span>
										<input
											className="aform-i"
											value={form.slug ?? ""}
											onChange={(e) =>
												setForm((f) => ({ ...f, slug: e.target.value }))
											}
										/>
									</div>
								</FormRow>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 12,
									}}
								>
									<FormRow label="Icône (emoji)">
										<input
											className="aform-i"
											value={form.iconEmoji ?? ""}
											onChange={(e) =>
												setForm((f) => ({ ...f, iconEmoji: e.target.value }))
											}
											style={{ textAlign: "center", fontSize: 18 }}
										/>
									</FormRow>
									<FormRow label="Couleur d'accent">
										<div className="aform-with-prefix">
											<span
												style={{
													padding: "0 8px",
													background: form.accentColor ?? "#C6563A",
												}}
											>
												&nbsp;
											</span>
											<input
												className="aform-i"
												value={form.accentColor ?? ""}
												onChange={(e) =>
													setForm((f) => ({
														...f,
														accentColor: e.target.value,
													}))
												}
												style={{
													fontFamily: "var(--a-font-mono)",
													fontSize: 12,
												}}
											/>
										</div>
									</FormRow>
								</div>

								<div className="divider" />
								<div className="eyebrow" style={{ marginBottom: 8 }}>
									Bannières
								</div>
								<FormRow
									label="Bannière large"
									helper="1920×1080 px (16:9) — tête de page sur ordinateur et dans l'app"
								>
									<CategoryImagePicker
										url={form.heroUrl ?? null}
										toast={toast}
										preview={{ width: 192, height: 108 }}
										altText={`Bannière ${form.name ?? detail.name}`}
										successMessage="Bannière large chargée."
										onChange={(media) =>
											setForm((f) => ({
												...f,
												heroMediaId: media?.id ?? null,
												heroUrl: media?.url ?? null,
											}))
										}
									/>
								</FormRow>
								<FormRow
									label="Bannière carrée"
									helper="1080×1080 px — tête de page sur mobile, cartes « à la une », grille de l'app"
								>
									<CategoryImagePicker
										url={form.heroMobileUrl ?? null}
										toast={toast}
										preview={{ width: 108, height: 108 }}
										altText={`Bannière carrée ${form.name ?? detail.name}`}
										successMessage="Bannière carrée chargée."
										onChange={(media) =>
											setForm((f) => ({
												...f,
												heroMobileMediaId: media?.id ?? null,
												heroMobileUrl: media?.url ?? null,
											}))
										}
									/>
								</FormRow>
								<FormRow
									label="Texte des bannières"
									helper="Recopie le texte écrit sur l'image : il est lu aux personnes malvoyantes et aux moteurs de recherche, qui ne voient pas l'image."
								>
									<input
										className="aform-i"
										maxLength={300}
										placeholder="Ex. Roule tranquille. Pièces, pneus et accessoires…"
										value={form.heroAlt ?? ""}
										onChange={(e) =>
											setForm((f) => ({ ...f, heroAlt: e.target.value }))
										}
									/>
								</FormRow>

								<div className="divider" />
								<div className="eyebrow" style={{ marginBottom: 8 }}>
									Visibilité
								</div>
								<div className="info-row">
									<span className="l">Active sur site</span>
									<span className="v">
										<AToggle
											on={form.isActive ?? true}
											onChange={(v: boolean) =>
												setForm((f) => ({ ...f, isActive: v }))
											}
										/>
									</span>
								</div>
								<div className="info-row">
									<span className="l">Visible app mobile</span>
									<span className="v">
										<AToggle
											on={form.isVisibleMobile ?? true}
											onChange={(v: boolean) =>
												setForm((f) => ({ ...f, isVisibleMobile: v }))
											}
										/>
									</span>
								</div>
								<div className="info-row">
									<span className="l">Mise en avant homepage</span>
									<span className="v">
										<AToggle
											on={form.isFeatured ?? false}
											onChange={(v: boolean) =>
												setForm((f) => ({ ...f, isFeatured: v }))
											}
										/>
									</span>
								</div>
								<div className="info-row" style={{ borderBottom: "none" }}>
									<span className="l">Recherche Meilisearch</span>
									<span className="v">
										<AToggle
											on={form.isSearchable ?? true}
											onChange={(v: boolean) =>
												setForm((f) => ({ ...f, isSearchable: v }))
											}
										/>
									</span>
								</div>

								<div className="divider" />
								<div className="eyebrow" style={{ marginBottom: 8 }}>
									SEO
								</div>
								<FormRow label="Titre meta">
									<input
										className="aform-i"
										value={form.metaTitle ?? ""}
										onChange={(e) =>
											setForm((f) => ({ ...f, metaTitle: e.target.value }))
										}
									/>
								</FormRow>
								<FormRow label="Description meta" helper="160 car. max">
									<textarea
										className="aform-i"
										rows={2}
										value={form.metaDescription ?? ""}
										onChange={(e) =>
											setForm((f) => ({
												...f,
												metaDescription: e.target.value,
											}))
										}
									/>
								</FormRow>
								<FormRow
									label="Mots-clés (keywords)"
									helper="Entrée pour ajouter"
								>
									<CatKeywordsEditor
										keywords={form.keywords ?? []}
										onChange={(k) => setForm((f) => ({ ...f, keywords: k }))}
									/>
								</FormRow>
								<FormRow
									label="Image de partage (Open Graph)"
									helper="~1200×630 px"
								>
									<CategoryImagePicker
										url={form.ogImageUrl ?? null}
										toast={toast}
										preview={{ width: 160, height: 84 }}
										altText="Image de partage catégorie"
										successMessage="Image de partage chargée."
										onChange={(media) =>
											setForm((f) => ({
												...f,
												ogImageMediaId: media?.id ?? null,
												ogImageUrl: media?.url ?? null,
											}))
										}
									/>
								</FormRow>
								<AiAssist<CategorySeoCopy>
									label="✨ Optimiser le SEO avec l'IA"
									panelTitle="Proposition SEO catégorie"
									disabled={!detail.name}
									run={(onText) =>
										aiApi.generateCategorySeoStream(
											{
												name: form.name ?? detail.name,
												parentName:
													detail.path.length > 1
														? detail.path[detail.path.length - 2]
														: null,
												level: detail.level,
												description: detail.description || null,
												keywords: form.keywords?.length ? form.keywords : null,
											},
											onText,
										)
									}
									onApply={(r) =>
										setForm((f) => ({
											...f,
											metaTitle: r.metaTitle,
											metaDescription: r.metaDescription,
											keywords: Array.from(
												new Set([...(f.keywords ?? []), ...r.keywords]),
											),
										}))
									}
									renderPreview={(r) => (
										<div style={{ display: "grid", gap: 8 }}>
											<div>
												<div className="muted" style={{ fontSize: 11 }}>
													Titre meta · {r.metaTitle.length} car.
												</div>
												<strong>{r.metaTitle}</strong>
											</div>
											<div>
												<div className="muted" style={{ fontSize: 11 }}>
													Description meta · {r.metaDescription.length} car.
												</div>
												<div>{r.metaDescription}</div>
											</div>
											<div>
												<div
													className="muted"
													style={{ fontSize: 11, marginBottom: 4 }}
												>
													Mots-clés suggérés (fusionnés aux existants)
												</div>
												<div
													style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
												>
													{r.keywords.map((t) => (
														<span key={t} className="tag neutral no-dot">
															#{t}
														</span>
													))}
												</div>
											</div>
										</div>
									)}
								/>

								{formError && (
									<div
										style={{
											color: "var(--a-danger)",
											fontSize: 12,
											marginTop: 8,
										}}
									>
										{formError}
									</div>
								)}

								<div className="cat-detail-actions">
									<AdminButton
										variant="ghost"
										onClick={() => setSelectedId(detail.id)}
									>
										Annuler
									</AdminButton>
									<AdminButton
										variant="primary"
										onClick={handleSave}
										disabled={updateMut.isPending}
									>
										<AdminIcon n="check" s={14} />
										{updateMut.isPending ? "Sauvegarde…" : "Enregistrer"}
									</AdminButton>
								</div>
								<AdminButton
									variant="ghost"
									onClick={handleDelete}
									disabled={deleteMut.isPending}
									style={{
										width: "100%",
										marginTop: 8,
										color: "var(--a-danger)",
									}}
								>
									<AdminIcon n="alert" s={14} /> Supprimer la catégorie
								</AdminButton>
							</div>
						</>
					)}
				</aside>
			</div>

			{menu && (
				<>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: menu backdrop */}
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: menu backdrop */}
					<div
						onClick={closeMenu}
						style={{ position: "fixed", inset: 0, zIndex: 40 }}
					/>
					<div
						role="menu"
						style={{
							position: "fixed",
							top: menu.top,
							left: menu.left,
							width: 210,
							background: "var(--a-surface)",
							border: "1px solid var(--a-border)",
							borderRadius: 8,
							boxShadow: "0 8px 24px -6px rgba(0,0,0,0.18)",
							zIndex: 41,
							padding: 4,
						}}
					>
						<button
							type="button"
							onClick={() => menuEdit(menu.node)}
							style={catMenuItem()}
							data-testid="cat-menu-edit"
						>
							<AdminIcon n="edit" s={14} />
							Modifier
						</button>
						{menu.node.level < 3 && (
							<button
								type="button"
								onClick={() => menuAddSub(menu.node)}
								style={catMenuItem()}
								data-testid="cat-menu-addsub"
							>
								<AdminIcon n="plus" s={14} />
								Ajouter une sous-catégorie
							</button>
						)}
						<button
							type="button"
							onClick={() => menuToggleActive(menu.node)}
							style={catMenuItem()}
							data-testid="cat-menu-toggle"
						>
							<AdminIcon n={menu.node.isActive ? "eyeOff" : "eye"} s={14} />
							{menu.node.isActive ? "Désactiver" : "Activer"}
						</button>
						{menu.node.archivedAt ? (
							<button
								type="button"
								onClick={() => doUnarchive(menu.node)}
								style={catMenuItem()}
								data-testid="cat-menu-unarchive"
							>
								<AdminIcon n="box" s={14} />
								Désarchiver
							</button>
						) : (
							<button
								type="button"
								onClick={() => doArchive(menu.node)}
								style={catMenuItem()}
								data-testid="cat-menu-archive"
							>
								<AdminIcon n="box" s={14} />
								Archiver
							</button>
						)}
						<button
							type="button"
							onClick={() => menuDelete(menu.node)}
							style={catMenuItem(true)}
							data-testid="cat-menu-delete"
						>
							<AdminIcon n="close" s={14} />
							Supprimer
						</button>
					</div>
				</>
			)}

			{deleteTarget && (
				<DeleteCategoryModal
					node={deleteTarget}
					toast={toast}
					onClose={() => setDeleteTarget(null)}
					onDeleted={() => {
						if (selectedId === deleteTarget.id) setSelectedId(null);
						setDeleteTarget(null);
					}}
				/>
			)}

			{createOpen && (
				<CategoryCreateModal
					parent={createParent}
					pending={createMut.isPending}
					onSubmit={submitCreate}
					onClose={() => setCreateOpen(false)}
				/>
			)}

			<style>{`
        .cat-layout {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 16px;
          align-items: start;
        }
        .cat-tree-head {
          display: flex; align-items: center;
          padding: 10px 18px;
          background: var(--a-surface-2);
          border-top: 1px solid var(--a-border);
          border-bottom: 1px solid var(--a-border);
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--a-ink-mute);
        }
        .cat-row {
          display: flex; align-items: center; gap: 10px;
          padding: 8px 14px 8px 0;
          border-bottom: 1px solid var(--a-border);
          cursor: pointer;
          transition: background 0.1s;
          position: relative;
        }
        .cat-row:hover { background: var(--a-surface-2); }
        .cat-row.selected { background: var(--a-terra-soft); }
        .cat-row.selected::before {
          content: ''; position: absolute; width: 3px; background: var(--a-terracotta);
          left: 0; top: 0; bottom: 0;
        }
        .cat-toggle { width: 16px; height: 16px; display: grid; place-items: center; color: var(--a-ink-mute); flex-shrink: 0; }
        .cat-toggle:hover { color: var(--a-ink); }
        .cat-grip { width: 14px; height: 14px; color: var(--a-ink-faint); cursor: grab; flex-shrink: 0; display: grid; place-items: center; }
        .cat-grip:hover { color: var(--a-ink-mute); }
        .cat-icon { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; flex-shrink: 0; }
        .cat-dot { width: 6px; height: 6px; border-radius: 50%; border: 1px solid; flex-shrink: 0; margin: 0 8px; }
        .cat-name { flex: 1; min-width: 0; display: flex; align-items: baseline; gap: 8px; }
        .cat-meta { font-size: 11px; }
        .cat-count { width: 90px; text-align: right; font-size: 12px; font-weight: 500; color: var(--a-ink-soft); flex-shrink: 0; }
        .cat-row .tag { width: 80px; justify-content: center; flex-shrink: 0; }

        .cat-detail { background: var(--a-surface); border: 1px solid var(--a-border); border-radius: var(--a-r); position: sticky; top: 76px; box-shadow: var(--a-shadow-sm); }
        .cat-detail-head { padding: 18px; border-bottom: 1px solid var(--a-border); background: var(--a-surface-2); }
        .cat-detail-head h3 { font-family: var(--a-font-display); font-weight: 700; font-size: 18px; letter-spacing: -0.01em; margin: 6px 0 4px; }
        .cat-crumb { display: flex; align-items: center; gap: 4px; font-size: 11px; color: var(--a-ink-mute); }
        .cat-crumb .sep { color: var(--a-ink-faint); display: inline-flex; }
        .cat-crumb .here { color: var(--a-ink); font-weight: 600; }
        .cat-detail-body { padding: 18px; }
        .mini-stat { padding: 10px 12px; background: var(--a-surface-2); border-radius: 6px; }
        .mini-stat .l { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: var(--a-ink-mute); font-weight: 600; }
        .mini-stat .v { font-family: var(--a-font-display); font-weight: 700; font-size: 18px; margin-top: 2px; }
        .cat-detail-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 14px; }
      `}</style>
		</>
	);
}

function catMenuItem(danger = false): React.CSSProperties {
	return {
		display: "flex",
		alignItems: "center",
		gap: 8,
		width: "100%",
		textAlign: "left",
		padding: "8px 10px",
		borderRadius: 6,
		fontSize: 13,
		background: "transparent",
		cursor: "pointer",
		color: danger ? "var(--a-danger)" : "var(--a-ink)",
	};
}

// ─── Sélecteur d'image de catégorie (upload + aperçu) ───────────────────────
// Sert à l'image de partage (Open Graph) et aux deux bannières.
function CategoryImagePicker({
	url,
	onChange,
	toast,
	preview,
	altText,
	successMessage,
}: {
	url: string | null;
	onChange: (media: { id: string; url: string } | null) => void;
	toast: ToastApi;
	preview: { width: number; height: number };
	altText: string;
	successMessage: string;
}) {
	const [busy, setBusy] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleFile = async (file: File) => {
		if (!file.type.startsWith("image/")) {
			toast.error("Choisis un fichier image.");
			return;
		}
		setBusy(true);
		try {
			const media: MediaAsset = await mediaApi.upload(file, { altText });
			onChange({ id: media.id, url: media.url });
			toast.success(successMessage);
		} catch (e) {
			toast.error(extractErrorMessage(e, "Upload impossible"));
		} finally {
			setBusy(false);
			if (inputRef.current) inputRef.current.value = "";
		}
	};

	return (
		<div style={{ display: "flex", gap: 12, alignItems: "center" }}>
			{url ? (
				<div style={{ position: "relative" }}>
					<img
						src={url}
						alt={`Aperçu : ${altText}`}
						style={{
							width: preview.width,
							height: preview.height,
							objectFit: "cover",
							borderRadius: 6,
							border: "1px solid var(--a-border)",
							display: "block",
						}}
					/>
					<button
						type="button"
						onClick={() => onChange(null)}
						aria-label="Retirer l'image"
						style={{
							position: "absolute",
							top: -8,
							right: -8,
							background: "var(--a-danger)",
							color: "#fff",
							borderRadius: "50%",
							width: 20,
							height: 20,
							display: "grid",
							placeItems: "center",
							cursor: "pointer",
						}}
					>
						<AdminIcon n="close" s={11} />
					</button>
				</div>
			) : (
				<div
					style={{
						width: preview.width,
						height: preview.height,
						borderRadius: 6,
						border: "1px dashed var(--a-border-strong)",
						display: "grid",
						placeItems: "center",
						color: "var(--a-ink-3)",
						fontSize: 11,
						textAlign: "center",
					}}
				>
					Aucune image
				</div>
			)}
			<div>
				<input
					ref={inputRef}
					type="file"
					accept="image/*"
					hidden
					onChange={(e) => {
						const f = e.target.files?.[0];
						if (f) void handleFile(f);
					}}
				/>
				<button
					type="button"
					className="btn btn-ghost"
					disabled={busy}
					onClick={() => inputRef.current?.click()}
				>
					{busy ? "Chargement…" : url ? "Remplacer" : "Choisir une image"}
				</button>
			</div>
		</div>
	);
}

// ─── Éditeur de mots-clés (chips + saisie) ──────────────────────────────────
function CatKeywordsEditor({
	keywords,
	onChange,
}: {
	keywords: string[];
	onChange: (next: string[]) => void;
}) {
	const [draft, setDraft] = useState("");
	const add = () => {
		const t = draft.trim().toLowerCase();
		if (t && !keywords.includes(t)) onChange([...keywords, t]);
		setDraft("");
	};
	return (
		<div
			style={{
				display: "flex",
				gap: 4,
				flexWrap: "wrap",
				padding: "6px 10px",
				background: "var(--a-bg)",
				border: "1px solid var(--a-border)",
				borderRadius: 6,
				minHeight: 34,
			}}
		>
			{keywords.map((k) => (
				<span
					key={k}
					className="tag neutral no-dot"
					style={{
						paddingRight: 4,
						display: "inline-flex",
						alignItems: "center",
					}}
				>
					#{k}{" "}
					<button
						type="button"
						onClick={() => onChange(keywords.filter((x) => x !== k))}
						style={{
							width: 14,
							height: 14,
							marginLeft: 4,
							opacity: 0.6,
							cursor: "pointer",
						}}
					>
						<AdminIcon n="close" s={10} />
					</button>
				</span>
			))}
			<input
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						add();
					}
				}}
				style={{
					border: "none",
					background: "transparent",
					outline: "none",
					flex: 1,
					minWidth: 80,
					fontSize: 12,
				}}
				placeholder="Ajouter un mot-clé SEO…"
			/>
		</div>
	);
}

// ─── Formulaire dédié : création d'une catégorie / sous-catégorie ────────────
function CategoryCreateModal({
	parent,
	pending,
	onSubmit,
	onClose,
}: {
	parent: { id: string; name: string } | null;
	pending: boolean;
	onSubmit: (name: string) => void;
	onClose: () => void;
}) {
	const [name, setName] = useState("");
	const valid = name.trim().length > 0;

	return (
		<Modal
			title={parent ? "Nouvelle sous-catégorie" : "Nouvelle catégorie"}
			subtitle={
				parent ? (
					<>
						Sera créée sous « <strong>{parent.name}</strong> ».
					</>
				) : (
					"Catégorie racine (niveau 1)."
				)
			}
			onClose={onClose}
		>
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (valid && !pending) onSubmit(name.trim());
				}}
			>
				<label
					htmlFor="cat-create-name"
					style={{
						display: "block",
						fontSize: 12,
						color: "var(--a-ink-soft)",
						marginBottom: 6,
					}}
				>
					Nom de la catégorie
				</label>
				<input
					id="cat-create-name"
					// biome-ignore lint/a11y/noAutofocus: modal default focus
					autoFocus
					className="aform-i"
					value={name}
					onChange={(e) => setName(e.target.value)}
					maxLength={120}
					placeholder="Ex. Smartphones, Accessoires audio…"
					style={{ width: "100%" }}
				/>
				<ModalFooter>
					<button type="button" className="btn btn-ghost" onClick={onClose}>
						Annuler
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={!valid || pending}
						style={{
							opacity: valid && !pending ? 1 : 0.5,
							cursor: valid && !pending ? "pointer" : "not-allowed",
						}}
					>
						{pending ? "Création…" : "Créer la catégorie"}
					</button>
				</ModalFooter>
			</form>
		</Modal>
	);
}

// ─── Formulaire dédié : suppression d'une catégorie (avec options) ──────────
const dmCard: React.CSSProperties = {
	border: "1px solid var(--a-border)",
	borderRadius: 8,
	padding: 12,
	display: "grid",
	gap: 8,
};

function DeleteCategoryModal({
	node,
	toast,
	onClose,
	onDeleted,
}: {
	node: Category;
	toast: ToastApi;
	onClose: () => void;
	onDeleted: () => void;
}) {
	const deleteMut = useDeleteCategory();
	const reassignMut = useReassignDeleteCategory();
	const deleteProductsMut = useDeleteCategoryWithProducts();
	const archiveMut = useArchiveCategory();

	const subCount = node.children?.length ?? 0;
	const hasProducts = node.productsCount > 0;
	const blocked = subCount > 0;

	const [query, setQuery] = useState("");
	const [results, setResults] = useState<Category[]>([]);
	const [target, setTarget] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [confirmName, setConfirmName] = useState("");

	useEffect(() => {
		if (target) return;
		const q = query.trim();
		if (q.length < 2) {
			setResults([]);
			return;
		}
		let cancelled = false;
		const t = window.setTimeout(async () => {
			try {
				const list = await categoriesApi.list({ search: q });
				if (!cancelled)
					setResults(list.filter((c) => c.id !== node.id).slice(0, 8));
			} catch {
				/* ignore */
			}
		}, 250);
		return () => {
			cancelled = true;
			window.clearTimeout(t);
		};
	}, [query, target, node.id]);

	const busy =
		deleteMut.isPending ||
		reassignMut.isPending ||
		deleteProductsMut.isPending ||
		archiveMut.isPending;
	const fail = (e: unknown, fb: string) =>
		toast.error(extractErrorMessage(e, fb));

	const doArchive = async () => {
		try {
			await archiveMut.mutateAsync(node.id);
			toast.success(`Catégorie « ${node.name} » archivée.`);
			onDeleted();
		} catch (e) {
			fail(e, "Archivage impossible");
		}
	};
	const doReassign = async () => {
		if (!target) return;
		try {
			const r = await reassignMut.mutateAsync({
				id: node.id,
				targetCategoryId: target.id,
			});
			toast.success(
				`${r.moved} produit(s) déplacé(s) vers « ${target.name} » · catégorie supprimée.`,
			);
			onDeleted();
		} catch (e) {
			fail(e, "Réaffectation impossible");
		}
	};
	const doDestructive = async () => {
		try {
			if (hasProducts) {
				const r = await deleteProductsMut.mutateAsync(node.id);
				toast.success(
					`Catégorie et ${r.deletedProducts} produit(s) supprimés.`,
				);
			} else {
				await deleteMut.mutateAsync(node.id);
				toast.success(`Catégorie « ${node.name} » supprimée.`);
			}
			onDeleted();
		} catch (e) {
			fail(e, "Suppression impossible");
		}
	};

	const nameOk = confirmName.trim() === node.name.trim();

	return (
		<Modal
			title={`Supprimer « ${node.name} »`}
			subtitle={`${node.productsCount} produit(s) · ${subCount} sous-catégorie(s)`}
			onClose={onClose}
			width={500}
		>
			{blocked ? (
				<>
					<div
						style={{
							padding: "10px 12px",
							background: "var(--a-warning-soft, #fef6e7)",
							color: "var(--a-warning, #9a6b00)",
							borderRadius: 8,
							fontSize: 13,
							lineHeight: 1.5,
						}}
					>
						Cette catégorie contient {subCount} sous-catégorie(s). Déplacez ou
						supprimez-les d'abord, ou archivez la catégorie (elle est conservée
						avec ses produits).
					</div>
					<ModalFooter>
						<button type="button" className="btn btn-ghost" onClick={onClose}>
							Annuler
						</button>
						<button
							type="button"
							className="btn btn-primary"
							disabled={busy}
							onClick={doArchive}
						>
							Archiver la catégorie
						</button>
					</ModalFooter>
				</>
			) : (
				<div style={{ display: "grid", gap: 12 }}>
					{hasProducts && (
						<section style={dmCard}>
							<strong style={{ fontSize: 13 }}>
								Réaffecter les {node.productsCount} produit(s)
							</strong>
							<span className="muted" style={{ fontSize: 12 }}>
								Déplace les produits vers une autre catégorie, puis supprime
								celle-ci. Recommandé.
							</span>
							{target ? (
								<div style={{ fontSize: 13 }}>
									Cible : <strong>{target.name}</strong>{" "}
									<button
										type="button"
										onClick={() => {
											setTarget(null);
											setQuery("");
										}}
										style={{
											color: "var(--a-terracotta)",
											cursor: "pointer",
											fontSize: 12,
										}}
									>
										changer
									</button>
								</div>
							) : (
								<div style={{ position: "relative" }}>
									<input
										className="aform-i"
										placeholder="Rechercher une catégorie cible…"
										value={query}
										onChange={(e) => setQuery(e.target.value)}
										style={{ width: "100%" }}
									/>
									{results.length > 0 && (
										<div
											style={{
												position: "absolute",
												top: "100%",
												left: 0,
												right: 0,
												zIndex: 5,
												marginTop: 2,
												background: "var(--a-surface)",
												border: "1px solid var(--a-border)",
												borderRadius: 8,
												boxShadow: "0 8px 24px -6px rgba(0,0,0,0.18)",
												maxHeight: 220,
												overflow: "auto",
											}}
										>
											{results.map((r) => (
												<button
													key={r.id}
													type="button"
													onClick={() => {
														setTarget({ id: r.id, name: r.name });
														setResults([]);
													}}
													style={{
														display: "block",
														width: "100%",
														textAlign: "left",
														padding: "8px 10px",
														fontSize: 13,
														cursor: "pointer",
														background: "transparent",
													}}
												>
													{r.iconEmoji ?? "·"} {r.name}{" "}
													<span className="muted" style={{ fontSize: 11 }}>
														· niveau {r.level}
													</span>
												</button>
											))}
										</div>
									)}
								</div>
							)}
							<button
								type="button"
								className="btn btn-primary"
								disabled={!target || busy}
								onClick={doReassign}
								style={{
									justifySelf: "start",
									opacity: target && !busy ? 1 : 0.5,
								}}
							>
								Réaffecter et supprimer
							</button>
						</section>
					)}

					<section style={dmCard}>
						<strong style={{ fontSize: 13, color: "var(--a-danger)" }}>
							Supprimer définitivement
							{hasProducts
								? ` (catégorie + ${node.productsCount} produit·s)`
								: ""}
						</strong>
						<span className="muted" style={{ fontSize: 12 }}>
							{hasProducts
								? "Les produits seront supprimés. Bloqué proprement si un produit est lié à une commande."
								: "Action définitive."}
						</span>
						<label className="muted" style={{ fontSize: 12 }}>
							Tapez{" "}
							<strong style={{ color: "var(--a-ink)" }}>{node.name}</strong>{" "}
							pour confirmer
						</label>
						<input
							className="aform-i"
							value={confirmName}
							onChange={(e) => setConfirmName(e.target.value)}
							placeholder={node.name}
							autoComplete="off"
							style={{ width: "100%" }}
						/>
						<button
							type="button"
							className="btn"
							disabled={!nameOk || busy}
							onClick={doDestructive}
							style={{
								justifySelf: "start",
								background: "var(--a-danger)",
								color: "#fff",
								opacity: nameOk && !busy ? 1 : 0.5,
								cursor: nameOk ? "pointer" : "not-allowed",
							}}
						>
							Supprimer définitivement
						</button>
					</section>

					<section style={dmCard}>
						<strong style={{ fontSize: 13 }}>Archiver plutôt</strong>
						<span className="muted" style={{ fontSize: 12 }}>
							Masque la catégorie sans rien supprimer (produits conservés).
						</span>
						<button
							type="button"
							className="btn btn-ghost"
							disabled={busy}
							onClick={doArchive}
							style={{ justifySelf: "start" }}
						>
							<AdminIcon n="box" s={14} /> Archiver la catégorie
						</button>
					</section>

					<ModalFooter>
						<button type="button" className="btn btn-ghost" onClick={onClose}>
							Annuler
						</button>
					</ModalFooter>
				</div>
			)}
		</Modal>
	);
}
